import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { format, parseISO, startOfWeek, endOfWeek, startOfMonth, endOfMonth, addDays, addWeeks, addMonths, subWeeks, subMonths, isSameDay, isSameMonth, startOfDay } from 'date-fns';
import { nb } from 'date-fns/locale';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, MapPin, User, Clock, Rss, Copy, Check, X, Download, Printer, List as ListIcon } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Default event types for display (synced with Calendar.js)
const DEFAULT_EVENT_TYPES = {
  training: { label: 'Trening', color: 'bg-blue-500' },
  dugnad: { label: 'Dugnad', color: 'bg-yellow-500' },
  safety_course: { label: 'Sikkerhetskurs', color: 'bg-green-500' },
  annual_meeting: { label: 'Årsmøte', color: 'bg-purple-500' },
  competition: { label: 'Stevne', color: 'bg-red-500' },
  other: { label: 'Annet', color: 'bg-gray-500' }
};

export default function CalendarEmbed() {
  const { orgId } = useParams();
  const [searchParams] = useSearchParams();
  const theme = searchParams.get('theme') || 'dark';
  const defaultView = searchParams.get('view') || null; // optional URL override (week/month/list)
  const isLight = theme === 'light';
  
  const [events, setEvents] = useState([]);
  const [organization, setOrganization] = useState(null);
  const [eventTypes, setEventTypes] = useState(DEFAULT_EVENT_TYPES);
  const [currentDate, setCurrentDate] = useState(new Date());
  const navigatedRef = useRef(false);
  // Calendar subscription modal
  const [showSubscribeModal, setShowSubscribeModal] = useState(false);
  const [copiedSubscribe, setCopiedSubscribe] = useState(false);

  // Build live subscription URLs - dynamic (reflects all changes in Kalender)
  const subscribeUrls = useMemo(() => {
    if (!orgId) return null;
    const httpsUrl = `${API_BASE}/api/calendar/ical/${orgId}`;
    const webcalUrl = httpsUrl.replace(/^https?:/, 'webcal:');
    return {
      https: httpsUrl,
      webcal: webcalUrl,
      // Google Calendar krever webcal://-schema via /render?cid=... — /r?cid=https:// gir "Sjekk nettadressen"
      google: `https://calendar.google.com/calendar/render?cid=${encodeURIComponent(webcalUrl)}`,
      outlookWeb: webcalUrl,
    };
  }, [orgId]);

  const copySubscribeLink = async (urlToCopy) => {
    try {
      await navigator.clipboard.writeText(urlToCopy);
      setCopiedSubscribe(true);
      setTimeout(() => setCopiedSubscribe(false), 2500);
    } catch (e) { /* ignore */ }
  };

  const downloadIcs = () => {
    if (subscribeUrls?.https) window.open(subscribeUrls.https, '_blank');
  };

  const [viewMode, setViewMode] = useState(defaultView || 'week'); // Will be updated from API org settings if no URL override
  const [orgDefaultApplied, setOrgDefaultApplied] = useState(!!defaultView); // skip applying if URL provided
  const [listPage, setListPage] = useState(1);
  const LIST_PAGE_SIZE = 30;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Theme classes
  const themeClasses = {
    bg: isLight ? 'bg-white' : 'bg-slate-900',
    text: isLight ? 'text-slate-800' : 'text-white',
    textMuted: isLight ? 'text-slate-500' : 'text-gray-400',
    textSubtle: isLight ? 'text-slate-400' : 'text-gray-500',
    border: isLight ? 'border-slate-200' : 'border-gray-700',
    cardBg: isLight ? 'bg-slate-50' : 'bg-slate-800/50',
    cardBgAlt: isLight ? 'bg-slate-100' : 'bg-slate-800',
    cardBgInactive: isLight ? 'bg-slate-50/50' : 'bg-slate-900/30',
    btnBg: isLight ? 'bg-slate-100 hover:bg-slate-200' : 'bg-slate-700 hover:bg-slate-600',
    btnActive: isLight ? 'bg-amber-500 text-white' : 'bg-yellow-500 text-black',
    todayBg: isLight ? 'bg-amber-50 border-amber-400' : 'bg-blue-900/30 border-blue-500',
    todayText: isLight ? 'text-amber-600' : 'text-blue-400',
    todayBadge: isLight ? 'bg-amber-500' : 'bg-blue-500',
    accentText: isLight ? 'text-amber-600' : 'text-yellow-500',
    linkText: isLight ? 'text-amber-600 hover:text-amber-700' : 'text-yellow-500 hover:underline'
  };

  // Fetch events
  useEffect(() => {
    const fetchEvents = async () => {
      if (!orgId) return;
      
      setLoading(true);
      try {
        // Calculate date range based on view
        let startDate, endDate;
        if (viewMode === 'week') {
          startDate = startOfWeek(currentDate, { weekStartsOn: 1 });
          endDate = endOfWeek(currentDate, { weekStartsOn: 1 });
        } else if (viewMode === 'list') {
          // Liste starter alltid fra dagens dato (lastes på nytt hver gang)
          startDate = startOfDay(new Date());
          endDate = addDays(startDate, 365);
        } else {
          startDate = startOfMonth(currentDate);
          endDate = endOfMonth(currentDate);
        }
        
        const response = await axios.get(`${API_BASE}/api/calendar/public/${orgId}`, {
          params: {
            start_date: startDate.toISOString(),
            end_date: endDate.toISOString()
          }
        });
        
        setEvents(response.data.events || []);
        setOrganization(response.data.organization);

        // Apply org default_view (only once, only if no URL override)
        if (!orgDefaultApplied && response.data.default_view) {
          const dv = response.data.default_view;
          if (['week', 'month', 'list'].includes(dv)) {
            setViewMode(dv);
          }
          setOrgDefaultApplied(true);
        }
        
        // Set event types from org settings if available
        const orgEventTypes = response.data.event_types || [];
        if (orgEventTypes.length > 0) {
          const typesObj = {};
          orgEventTypes.forEach(t => {
            typesObj[t.id] = {
              label: t.label,
              color: t.color,
              textColor: t.textColor || 'text-white',
              customColor: t.customColor,
              customTextColor: t.customTextColor
            };
          });
          setEventTypes(typesObj);
        }
        
        setError(null);
      } catch (err) {
        console.error('Error fetching calendar:', err);
        setError(err.response?.data?.detail || 'Kunne ikke laste kalenderen');
      } finally {
        setLoading(false);
      }
    };
    
    fetchEvents();
  }, [orgId, currentDate, viewMode]);

  // Auto-refresh every 60 seconds to keep embed in sync with Kalender changes
  useEffect(() => {
    const interval = setInterval(() => {
      // Følg dagens dato hvis besøkende ikke har bladd selv
      setCurrentDate(prev => (navigatedRef.current ? new Date(prev) : new Date()));
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  // Navigation
  const [todayScrollReq, setTodayScrollReq] = useState(0);
  const goToToday = () => {
    navigatedRef.current = false;
    setListPage(1);
    if (viewMode === 'list') setTodayScrollReq(n => n + 1);
    else setCurrentDate(new Date());
  };

  useEffect(() => {
    if (!todayScrollReq) return;
    const key = format(new Date(), 'yyyy-MM-dd');
    setTimeout(() => {
      const el = [...document.querySelectorAll('[data-list-date]')].find(n => n.dataset.listDate >= key);
      el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  }, [todayScrollReq]);
  const goToPrev = () => {
    navigatedRef.current = true;
    if (viewMode === 'week') {
      setCurrentDate(prev => subWeeks(prev, 1));
    } else {
      setCurrentDate(prev => subMonths(prev, 1));
    }
  };
  const goToNext = () => {
    navigatedRef.current = true;
    if (viewMode === 'week') {
      setCurrentDate(prev => addWeeks(prev, 1));
    } else {
      setCurrentDate(prev => addMonths(prev, 1));
    }
  };

  // Get week days
  const getWeekDays = () => {
    const start = startOfWeek(currentDate, { weekStartsOn: 1 });
    return Array.from({ length: 7 }, (_, i) => addDays(start, i));
  };

  // Get events for a specific day (deduplicated)
  const getEventsForDay = (date) => {
    const dayEvents = events.filter(event => {
      const eventDate = parseISO(event.start_datetime);
      return isSameDay(eventDate, date);
    });
    
    const seen = new Set();
    return dayEvents.filter(event => {
      const uniqueKey = event.instance_id || event.id;
      if (seen.has(uniqueKey)) return false;
      seen.add(uniqueKey);
      return true;
    });
  };

  // Format time using local hours
  const formatEventTime = (event, type = 'start') => {
    if (type === 'start' && event.local_start_hour !== undefined) {
      return `${String(event.local_start_hour).padStart(2, '0')}:${String(event.local_start_minute || 0).padStart(2, '0')}`;
    }
    if (type === 'end' && event.local_end_hour !== undefined) {
      return `${String(event.local_end_hour).padStart(2, '0')}:${String(event.local_end_minute || 0).padStart(2, '0')}`;
    }
    const dt = type === 'start' ? event.start_datetime : event.end_datetime;
    return dt ? format(parseISO(dt), 'HH:mm') : '';
  };

  // Get event type info - use org's custom types first, fallback to defaults
  const getEventType = (eventTypeId) => {
    // First check if we have custom event type from org settings
    if (eventTypes[eventTypeId]) {
      const et = eventTypes[eventTypeId];
      return {
        label: et.label,
        color: et.color,
        textColor: et.textColor || 'text-white',
        customColor: et.customColor,
        customTextColor: et.customTextColor
      };
    }
    // Fallback to default
    return DEFAULT_EVENT_TYPES[eventTypeId] || DEFAULT_EVENT_TYPES.other || { label: eventTypeId, color: 'bg-gray-500', textColor: 'text-white' };
  };

  // Render list view (paginated)
  const renderListView = () => {
    const sorted = [...events].sort((a, b) => new Date(a.start_datetime) - new Date(b.start_datetime));
    const totalPages = Math.max(1, Math.ceil(sorted.length / LIST_PAGE_SIZE));
    const safePage = Math.min(listPage, totalPages);
    const startIdx = (safePage - 1) * LIST_PAGE_SIZE;
    const pageEvents = sorted.slice(startIdx, startIdx + LIST_PAGE_SIZE);

    const byDate = {};
    pageEvents.forEach(ev => {
      const key = format(parseISO(ev.start_datetime), 'yyyy-MM-dd');
      if (!byDate[key]) byDate[key] = [];
      byDate[key].push(ev);
    });

    if (sorted.length === 0) {
      return (
        <div className={`text-center py-12 ${themeClasses.textMuted}`}>
          <CalendarIcon size={36} className="mx-auto mb-2 opacity-50" />
          <p>Ingen aktiviteter</p>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <div className={`text-xs ${themeClasses.textMuted} no-print`}>
          Viser {startIdx + 1}–{Math.min(startIdx + LIST_PAGE_SIZE, sorted.length)} av {sorted.length}
        </div>

        {Object.entries(byDate).map(([dateKey, evs]) => {
          const dateObj = parseISO(dateKey);
          const isCurrentDay = isSameDay(dateObj, new Date());
          return (
            <div key={dateKey} data-list-date={dateKey} className={`rounded-lg p-3 border ${isCurrentDay ? themeClasses.todayBg : `${themeClasses.cardBg} ${themeClasses.border}`}`}>
              <div className="flex items-baseline gap-2 mb-2">
                <span className={`text-base font-semibold capitalize ${isCurrentDay ? themeClasses.todayText : themeClasses.text}`}>
                  {format(dateObj, 'EEEE d. MMMM yyyy', { locale: nb })}
                </span>
                {isCurrentDay && <span className={`text-xs ${themeClasses.todayBadge} text-white px-2 py-0.5 rounded`}>I dag</span>}
              </div>
              <div className="space-y-2">
                {evs.map((event, idx) => {
                  const eventType = getEventType(event.event_type);
                  const borderColor = eventType.customColor ? eventType.customColor : undefined;
                  const borderClass = eventType.customColor ? '' : eventType.color.replace('bg-', 'border-');
                  const pillBgClass = eventType.customColor ? '' : eventType.color;
                  const pillStyle = eventType.customColor ? { backgroundColor: eventType.customColor } : {};
                  const cardBg = isLight ? 'bg-slate-100/80' : 'bg-slate-800/60';
                  const isImportant = !!event.is_important;
                  return (
                    <div
                      key={idx}
                      className={isImportant
                        ? `${cardBg} ring-2 ring-red-500/70 border-l-[6px] border-red-500 rounded-r-lg p-2 shadow-md shadow-red-500/20`
                        : `${cardBg} border-l-4 ${borderClass} rounded-r-lg p-2`
                      }
                      style={!isImportant && borderColor ? { borderLeftColor: borderColor } : {}}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className={`font-medium text-sm ${themeClasses.text} truncate`}>{event.title}</p>
                            {isImportant && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-500 text-white font-bold">VIKTIG</span>
                            )}
                          </div>
                          {event.description && (
                            <p className={`text-xs ${themeClasses.textMuted} mt-1 whitespace-pre-wrap break-words line-clamp-2`}>{event.description}</p>
                          )}
                          <div className={`flex flex-wrap items-center gap-x-4 gap-y-1 text-xs ${themeClasses.textMuted} mt-1`}>
                            <span className="flex items-center gap-1 whitespace-nowrap">
                              <Clock size={11} />
                              <span className={`font-semibold ${themeClasses.text}`}>{formatEventTime(event, 'start')} - {formatEventTime(event, 'end')}</span>
                            </span>
                            {event.location && (
                              <span className="flex items-center gap-1 whitespace-nowrap"><MapPin size={11} />{event.location}</span>
                            )}
                            {event.skyteleder_name && (
                              <span className="flex items-center gap-1 whitespace-nowrap"><User size={11} />{event.skyteleder_name}</span>
                            )}
                          </div>
                        </div>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${pillBgClass} text-white whitespace-nowrap flex-shrink-0`} style={pillStyle}>
                          {eventType.label}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}

        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-3 pt-2 no-print" data-testid="embed-list-pagination">
            <button
              onClick={() => setListPage(p => Math.max(1, p - 1))}
              disabled={safePage <= 1}
              className={`px-4 py-2 text-sm rounded ${themeClasses.btnBg} disabled:opacity-40 disabled:cursor-not-allowed`}
            >
              ← Forrige
            </button>
            <span className={`text-sm ${themeClasses.textMuted}`}>Side {safePage} av {totalPages}</span>
            <button
              onClick={() => setListPage(p => Math.min(totalPages, p + 1))}
              disabled={safePage >= totalPages}
              className={`px-4 py-2 text-sm rounded ${themeClasses.btnBg} disabled:opacity-40 disabled:cursor-not-allowed`}
            >
              Neste →
            </button>
          </div>
        )}
      </div>
    );
  };

  // Render week view
  const renderWeekView = () => {
    const weekDays = getWeekDays();
    
    return (
      <div className="space-y-1">
        {weekDays.map((day, idx) => {
          const dayEvents = getEventsForDay(day);
          const isToday = isSameDay(day, new Date());
          
          return (
            <div key={idx} className={`rounded-lg p-2 border ${isToday ? themeClasses.todayBg : `${themeClasses.cardBg} ${themeClasses.border}`}`}>
              <div className="flex items-center gap-2 mb-1">
                <span className={`text-base font-bold ${isToday ? themeClasses.todayText : themeClasses.text}`}>
                  {format(day, 'd', { locale: nb })}
                </span>
                <span className={`text-sm ${themeClasses.textMuted} capitalize`}>
                  {format(day, 'EEEE', { locale: nb })}
                </span>
                {isToday && <span className={`text-xs ${themeClasses.todayBadge} text-white px-2 py-0.5 rounded`}>I dag</span>}
              </div>
              
              {dayEvents.length > 0 ? (
                <div className="space-y-1">
                  {dayEvents.map((event, eventIdx) => {
                    const eventType = getEventType(event.event_type);
                    const borderColor = eventType.customColor 
                      ? eventType.customColor
                      : undefined;
                    const borderClass = eventType.customColor ? '' : eventType.color.replace('bg-', 'border-');
                    const pillBgClass = eventType.customColor ? '' : eventType.color;
                    const pillStyle = eventType.customColor ? { backgroundColor: eventType.customColor } : {};
                    const cardBg = isLight ? 'bg-slate-100/80' : 'bg-slate-800/60';
                    const isImportant = !!event.is_important;
                    
                    return (
                      <div 
                        key={eventIdx} 
                        className={
                          isImportant
                            ? `${cardBg} ring-2 ring-red-500/70 border-l-[6px] border-red-500 rounded-r-lg p-2 shadow-md shadow-red-500/20`
                            : `${cardBg} border-l-4 ${borderClass} rounded-r-lg p-2`
                        }
                        style={!isImportant && borderColor ? { borderLeftColor: borderColor } : {}}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className={`font-medium text-sm ${themeClasses.text} truncate`}>{event.title}</p>
                              {isImportant && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-500 text-white font-bold tracking-wide whitespace-nowrap">
                                  VIKTIG
                                </span>
                              )}
                            </div>
                            {event.description && (
                              <p className={`text-xs ${themeClasses.textMuted} mt-1 whitespace-pre-wrap break-words line-clamp-2`}>
                                {event.description}
                              </p>
                            )}
                            <div className={`flex flex-wrap items-center gap-x-4 gap-y-1 text-xs ${themeClasses.textMuted} mt-1`}>
                              <span className="flex items-center gap-1 whitespace-nowrap">
                                <Clock size={11} />
                                <span className={`font-semibold ${themeClasses.text}`}>{formatEventTime(event, 'start')} - {formatEventTime(event, 'end')}</span>
                              </span>
                              {event.location && (
                                <span className="flex items-center gap-1 whitespace-nowrap">
                                  <MapPin size={11} />
                                  {event.location}
                                </span>
                              )}
                              {event.skyteleder_name && (
                                <span className="flex items-center gap-1 whitespace-nowrap">
                                  <User size={11} />
                                  {event.skyteleder_name}
                                </span>
                              )}
                            </div>
                          </div>
                          <span 
                            className={`text-xs px-2 py-0.5 rounded-full ${pillBgClass} text-white whitespace-nowrap flex-shrink-0`}
                            style={pillStyle}
                          >
                            {eventType.label}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className={`${themeClasses.textSubtle} text-xs`}>Ingen aktiviteter</div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // Render month view
  const renderMonthView = () => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 });
    const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
    
    const weeks = [];
    let currentWeekStart = calendarStart;
    
    while (currentWeekStart <= calendarEnd) {
      const week = Array.from({ length: 7 }, (_, i) => addDays(currentWeekStart, i));
      weeks.push(week);
      currentWeekStart = addDays(currentWeekStart, 7);
    }
    
    return (
      <div>
        {/* Day headers */}
        <div className="grid grid-cols-7 gap-1 mb-1">
          {['Man', 'Tir', 'Ons', 'Tor', 'Fre', 'Lør', 'Søn'].map(day => (
            <div key={day} className={`text-center text-xs ${themeClasses.textMuted} py-1 font-medium`}>{day}</div>
          ))}
        </div>
        
        {/* Calendar grid */}
        {weeks.map((week, weekIdx) => (
          <div key={weekIdx} className="grid grid-cols-7 gap-1 mb-1">
            {week.map((day, dayIdx) => {
              const dayEvents = getEventsForDay(day);
              const isToday = isSameDay(day, new Date());
              const isCurrentMonth = isSameMonth(day, currentDate);
              
              return (
                <div 
                  key={dayIdx} 
                  className={`min-h-[80px] p-1 rounded border ${
                    isToday ? themeClasses.todayBg : 
                    isCurrentMonth ? `${themeClasses.cardBg} ${themeClasses.border}` : `${themeClasses.cardBgInactive} ${themeClasses.border}`
                  }`}
                >
                  <div className={`text-xs mb-1 font-medium ${
                    isToday ? themeClasses.todayText :
                    isCurrentMonth ? themeClasses.text : themeClasses.textSubtle
                  }`}>
                    {format(day, 'd')}
                  </div>
                  <div className="space-y-0.5">
                    {dayEvents.slice(0, 3).map((event, eventIdx) => {
                      const eventType = getEventType(event.event_type);
                      const bgStyle = eventType.customColor ? { backgroundColor: eventType.customColor } : {};
                      const textStyle = eventType.customTextColor && !eventType.textColor?.startsWith('text-') 
                        ? { color: eventType.customTextColor } 
                        : {};
                      const textClass = eventType.customTextColor ? '' : (eventType.textColor || 'text-white');
                      
                      const tooltipText = event.skyteleder_name 
                        ? `${event.title} (${formatEventTime(event, 'start')} - ${formatEventTime(event, 'end')}) - Skyteleder: ${event.skyteleder_name}`
                        : `${event.title} (${formatEventTime(event, 'start')} - ${formatEventTime(event, 'end')})`;
                      return (
                        <div 
                          key={eventIdx} 
                          className={`${eventType.customColor ? '' : eventType.color} rounded px-1 py-0.5 ${textClass} text-xs truncate`}
                          style={{...bgStyle, ...textStyle}}
                          title={tooltipText}
                        >
                          {event.title}
                        </div>
                      );
                    })}
                    {dayEvents.length > 3 && (
                      <div className={`text-xs ${themeClasses.textMuted}`}>+{dayEvents.length - 3} flere</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    );
  };

  if (error) {
    return (
      <div className={`min-h-screen ${themeClasses.bg} flex items-center justify-center p-4`}>
        <div className={`${isLight ? 'text-red-600' : 'text-red-400'} text-center`}>
          <CalendarIcon size={48} className="mx-auto mb-4 opacity-50" />
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${themeClasses.bg} ${themeClasses.text} p-4 calendar-print-root`}>
      {/* Print-style: kun selve kalenderen skrives ut, i sort/hvitt */}
      <style>{`
        @media print {
          @page { margin: 1cm; size: A4 portrait; }
          body * { visibility: hidden !important; }
          .calendar-print-root, .calendar-print-root * { visibility: visible !important; }
          .calendar-print-root { position: absolute !important; left: 0 !important; top: 0 !important; width: 100% !important; background: #ffffff !important; color: #000000 !important; }
          .no-print, [data-testid="embed-subscribe-modal"] { display: none !important; visibility: hidden !important; }
          .calendar-print-root * {
            background: #ffffff !important;
            background-image: none !important;
            background-color: #ffffff !important;
            color: #000000 !important;
            box-shadow: none !important;
            text-shadow: none !important;
          }
          .calendar-print-root [class*="border"] { border-color: #000000 !important; }
          .calendar-print-root [class*="rounded-full"] {
            border: 1px solid #000000 !important;
            color: #000000 !important;
            font-weight: 600 !important;
          }
        }
      `}</style>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <CalendarIcon size={24} className={themeClasses.accentText} />
            <h1 className={`text-xl font-bold ${themeClasses.text}`}>{organization?.name || 'Kalender'}</h1>
          </div>
          
          <div className="flex items-center gap-2 no-print">
            <button
              onClick={() => { setViewMode('week'); setListPage(1); }}
              className={`px-3 py-1 text-sm rounded transition-colors ${viewMode === 'week' ? themeClasses.btnActive : themeClasses.btnBg}`}
              data-testid="embed-view-week"
            >
              Uke
            </button>
            <button
              onClick={() => { setViewMode('month'); setListPage(1); }}
              className={`px-3 py-1 text-sm rounded transition-colors ${viewMode === 'month' ? themeClasses.btnActive : themeClasses.btnBg}`}
              data-testid="embed-view-month"
            >
              Mnd
            </button>
            <button
              onClick={() => { setViewMode('list'); setListPage(1); }}
              className={`px-3 py-1 text-sm rounded transition-colors ${viewMode === 'list' ? themeClasses.btnActive : themeClasses.btnBg}`}
              data-testid="embed-view-list"
            >
              Liste
            </button>
            <button
              onClick={() => window.print()}
              className={`px-3 py-1 text-sm rounded transition-colors flex items-center gap-1 no-print ${themeClasses.btnBg}`}
              title="Skriv ut kalenderen (svart/hvitt)"
              data-testid="embed-print-btn"
            >
              <Printer size={14} />
            </button>
            <button
              onClick={() => setShowSubscribeModal(true)}
              className={`px-3 py-1 text-sm rounded transition-colors flex items-center gap-1 no-print ${themeClasses.btnBg}`}
              title="Abonnér på kalenderen (live oppdateringer)"
              data-testid="embed-subscribe-btn"
            >
              <Rss size={14} />
              <span>Abonnér</span>
            </button>
          </div>
        </div>
        
        {/* Navigation */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <button onClick={goToPrev} className={`p-2 rounded ${themeClasses.btnBg}`}>
              <ChevronLeft size={20} />
            </button>
            <button onClick={goToToday} className={`px-3 py-2 rounded text-sm ${themeClasses.btnBg}`}>
              I dag
            </button>
            <button onClick={goToNext} className={`p-2 rounded ${themeClasses.btnBg}`}>
              <ChevronRight size={20} />
            </button>
          </div>
          
          <div className={`text-lg font-medium capitalize ${themeClasses.text}`}>
            {viewMode === 'week' 
              ? `Uke ${format(currentDate, 'w', { locale: nb })} - ${format(currentDate, 'MMMM yyyy', { locale: nb })}`
              : viewMode === 'list'
                ? 'Liste'
                : format(currentDate, 'MMMM yyyy', { locale: nb })
            }
          </div>
        </div>
        
        {/* Calendar content */}
        {loading ? (
          <div className={`text-center py-12 ${themeClasses.textMuted}`}>Laster kalender...</div>
        ) : (
          viewMode === 'week' ? renderWeekView() : viewMode === 'list' ? renderListView() : renderMonthView()
        )}
        
        {/* Footer */}
        <div className={`mt-4 text-center text-xs ${themeClasses.textSubtle}`}>
          Powered by <a href="https://aktivlogg.no" target="_blank" rel="noopener noreferrer" className={themeClasses.linkText}>Aktivlogg</a>
        </div>
      </div>

      {/* Subscribe Modal - tema-tilpasset for både lys og mørk bakgrunn */}
      {showSubscribeModal && subscribeUrls && (
        <div
          className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setShowSubscribeModal(false); }}
          data-testid="embed-subscribe-modal"
        >
          <div className={`${isLight ? 'bg-white border-yellow-500/40' : 'bg-gray-800 border-yellow-500/40'} border rounded-xl w-full max-w-md max-h-[90vh] overflow-y-auto shadow-xl`}>
            <div className={`${isLight ? 'bg-white border-gray-200' : 'bg-gray-800 border-gray-700'} sticky top-0 border-b px-5 py-4 flex items-center justify-between`}>
              <div className="flex items-center gap-3">
                <div className="bg-yellow-500/20 p-2 rounded-full">
                  <Rss size={20} className="text-yellow-500" />
                </div>
                <div>
                  <h3 className={`font-bold text-lg ${isLight ? 'text-gray-900' : 'text-white'}`}>Abonnér på kalender</h3>
                  <p className={`text-xs ${isLight ? 'text-gray-500' : 'text-gray-400'}`}>Velg din kalender-app for live oppdateringer</p>
                </div>
              </div>
              <button
                onClick={() => setShowSubscribeModal(false)}
                className={`${isLight ? 'text-gray-500 hover:text-gray-900' : 'text-gray-400 hover:text-white'} p-1`}
                aria-label="Lukk"
              >
                <X size={18} />
              </button>
            </div>

            <div className="p-5 space-y-2">
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 mb-3">
                <p className={`text-xs ${isLight ? 'text-yellow-800' : 'text-yellow-200'}`}>
                  <strong>Live oppdateringer:</strong> Endringer i klubbens kalender vises automatisk i din lokale kalender innen kort tid.
                </p>
              </div>

              {/* Google Calendar */}
              <a
                href={subscribeUrls.google}
                target="_blank"
                rel="noopener noreferrer"
                className={`flex items-center gap-3 w-full p-3 rounded-lg border transition-colors ${isLight ? 'bg-gray-50 hover:bg-gray-100 border-gray-200 hover:border-yellow-500' : 'bg-gray-700 hover:bg-gray-600 border-gray-600 hover:border-yellow-500'}`}
                data-testid="embed-subscribe-google"
              >
                <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center flex-shrink-0 border border-gray-200">
                  <span className="text-blue-600 font-bold text-xs">31</span>
                </div>
                <div className="flex-1 text-left">
                  <div className={`font-medium text-sm ${isLight ? 'text-gray-900' : 'text-white'}`}>Legg til i Google Kalender</div>
                  <div className={`text-xs ${isLight ? 'text-gray-500' : 'text-gray-400'}`}>Åpnes i nettleseren</div>
                </div>
                <span className="text-yellow-500 text-xs">→</span>
              </a>

              {/* Apple Calendar */}
              <a
                href={subscribeUrls.webcal}
                className={`flex items-center gap-3 w-full p-3 rounded-lg border transition-colors ${isLight ? 'bg-gray-50 hover:bg-gray-100 border-gray-200 hover:border-yellow-500' : 'bg-gray-700 hover:bg-gray-600 border-gray-600 hover:border-yellow-500'}`}
                data-testid="embed-subscribe-apple"
              >
                <div className="w-10 h-10 rounded-lg bg-gray-900 flex items-center justify-center flex-shrink-0">
                  <span className="text-white text-lg"></span>
                </div>
                <div className="flex-1 text-left">
                  <div className={`font-medium text-sm ${isLight ? 'text-gray-900' : 'text-white'}`}>Legg til i Apple Kalender</div>
                  <div className={`text-xs ${isLight ? 'text-gray-500' : 'text-gray-400'}`}>Åpner Apple Kalender-appen</div>
                </div>
                <span className="text-yellow-500 text-xs">→</span>
              </a>

              {/* Outlook */}
              <a
                href={subscribeUrls.outlookWeb}
                className={`flex items-center gap-3 w-full p-3 rounded-lg border transition-colors ${isLight ? 'bg-gray-50 hover:bg-gray-100 border-gray-200 hover:border-yellow-500' : 'bg-gray-700 hover:bg-gray-600 border-gray-600 hover:border-yellow-500'}`}
                data-testid="embed-subscribe-outlook"
              >
                <div className="w-10 h-10 rounded-lg bg-[#0078D4] flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-sm">O</span>
                </div>
                <div className="flex-1 text-left">
                  <div className={`font-medium text-sm ${isLight ? 'text-gray-900' : 'text-white'}`}>Legg til i Outlook</div>
                  <div className={`text-xs ${isLight ? 'text-gray-500' : 'text-gray-400'}`}>Outlook web / Microsoft 365</div>
                </div>
                <span className="text-yellow-500 text-xs">→</span>
              </a>

              {/* Manual webcal URL */}
              <div className={`border rounded-lg p-3 ${isLight ? 'border-gray-200 bg-gray-50' : 'border-gray-600 bg-gray-700/50'}`}>
                <div className="flex items-center gap-3 mb-2">
                  <CalendarIcon size={20} className="text-yellow-500 flex-shrink-0" />
                  <div className="flex-1">
                    <div className={`font-medium text-sm ${isLight ? 'text-gray-900' : 'text-white'}`}>Annen kalender-app</div>
                    <div className={`text-xs ${isLight ? 'text-gray-500' : 'text-gray-400'}`}>Kopier webcal-lenken</div>
                  </div>
                </div>
                <div className={`flex items-center gap-2 rounded p-2 border ${isLight ? 'bg-white border-gray-200' : 'bg-gray-900 border-gray-700'}`}>
                  <code className={`flex-1 text-xs truncate ${isLight ? 'text-gray-800' : 'text-yellow-200'}`}>{subscribeUrls.webcal}</code>
                  <button
                    onClick={() => copySubscribeLink(subscribeUrls.webcal)}
                    className="px-2 py-1 bg-yellow-500 hover:bg-yellow-400 text-gray-900 text-xs font-medium rounded flex items-center gap-1 flex-shrink-0"
                    data-testid="embed-subscribe-copy"
                  >
                    {copiedSubscribe ? <Check size={12} /> : <Copy size={12} />}
                    {copiedSubscribe ? 'Kopiert!' : 'Kopier'}
                  </button>
                </div>
              </div>

              {/* Last ned .ics */}
              <button
                onClick={() => { downloadIcs(); setShowSubscribeModal(false); }}
                className={`flex items-center gap-3 w-full p-3 rounded-lg border transition-colors ${isLight ? 'bg-gray-50 hover:bg-gray-100 border-gray-200' : 'bg-gray-700/40 hover:bg-gray-700 border-gray-700'}`}
              >
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${isLight ? 'bg-gray-200' : 'bg-gray-600'}`}>
                  <Download size={18} className={isLight ? 'text-gray-700' : 'text-gray-300'} />
                </div>
                <div className="flex-1 text-left">
                  <div className={`font-medium text-sm ${isLight ? 'text-gray-900' : 'text-white'}`}>Last ned som .ics-fil</div>
                  <div className={`text-xs ${isLight ? 'text-gray-500' : 'text-gray-400'}`}>Statisk engangs-fil</div>
                </div>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
